import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Storefront, Users, CaretDown, CaretUp, CheckCircle, Clock, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
};

const BonusTrackerPage = () => {
  const [tab, setTab] = useState('vendor');
  const [trackers, setTrackers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const fetchTrackers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/bonus-tracker?tracker_type=${tab}`, { withCredentials: true });
      setTrackers(res.data);
    } catch (error) {
      toast.error('Failed to load bonus tracker');
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchTrackers(); }, [fetchTrackers]);

  const toggleExpand = (idx) => setExpandedIdx(expandedIdx === idx ? null : idx);

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="bonus-tracker-page">
      <div>
        <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Bonus Stage Tracker</h1>
        <p className="text-sm text-slate-500 mt-1">Track 6-stage vendor and client bonus progress across all placements</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={tab === 'vendor' ? 'default' : 'outline'}
          onClick={() => { setTab('vendor'); setExpandedIdx(null); }}
          className="gap-2"
          data-testid="tab-vendor-tracker"
        >
          <Storefront size={18} /> Vendor Bonuses
        </Button>
        <Button
          variant={tab === 'client' ? 'default' : 'outline'}
          onClick={() => { setTab('client'); setExpandedIdx(null); }}
          className="gap-2"
          data-testid="tab-client-tracker"
        >
          <Users size={18} /> Client Bonuses
        </Button>
      </div>

      {/* Summary */}
      {!loading && trackers.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-medium">Active {tab === 'vendor' ? 'Vendors' : 'Clients'}</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{trackers.length}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-medium">Total Earned</p>
              <p className="text-3xl font-bold text-emerald-700 mt-1">{formatCurrency(trackers.reduce((s, t) => s + t.total_earned, 0))}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-4 text-center">
              <p className="text-xs text-slate-500 uppercase font-medium">Remaining</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">{formatCurrency(trackers.reduce((s, t) => s + t.remaining, 0))}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tracker Cards */}
      {loading ? (
        <Card><CardContent className="p-12 text-center text-slate-500">Loading bonus data...</CardContent></Card>
      ) : trackers.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-500">No {tab === 'vendor' ? 'new vendor' : 'new client'} bonuses found in the last 4 quarters</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {trackers.map((tracker, idx) => (
            <Card key={idx} className="border-slate-200 overflow-hidden" data-testid={`tracker-card-${idx}`}>
              {/* Header row */}
              <div
                className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpand(idx)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tab === 'vendor' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                    {tab === 'vendor' ? <Storefront size={20} className="text-blue-700" /> : <Users size={20} className="text-purple-700" />}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{tracker.entity_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className={
                        tracker.entity_type === 'new' ? 'bg-emerald-100 text-emerald-700' :
                        tracker.entity_type === 'existing_new' ? 'bg-cyan-100 text-cyan-700' :
                        'bg-purple-100 text-purple-700'
                      }>
                        {tracker.entity_type === 'new' ? 'New Vendor' :
                         tracker.entity_type === 'existing_new' ? 'Existing (New)' :
                         tracker.entity_type === 'new_client' ? 'New Client' : tracker.entity_type}
                      </Badge>
                      {tracker.company && <span className="text-xs text-slate-400">{tracker.company}</span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {/* Stage progress dots */}
                  <div className="flex items-center gap-1.5">
                    {[1,2,3,4,5,6].map(s => (
                      <div
                        key={s}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          s <= tracker.stages_completed
                            ? 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {s <= tracker.stages_completed ? <CheckCircle size={14} weight="bold" /> : s}
                      </div>
                    ))}
                  </div>

                  {/* Amount */}
                  <div className="text-right min-w-[120px]">
                    <p className="font-bold text-emerald-700">{formatCurrency(tracker.total_earned)}</p>
                    <p className="text-xs text-slate-400">of {formatCurrency(tracker.max_total)}</p>
                  </div>

                  {/* Progress bar */}
                  <div className="w-24">
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className="bg-emerald-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min((tracker.total_earned / tracker.max_total) * 100, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-400 text-center mt-0.5">{Math.round((tracker.total_earned / tracker.max_total) * 100)}%</p>
                  </div>

                  {expandedIdx === idx ? <CaretUp size={18} className="text-slate-400" /> : <CaretDown size={18} className="text-slate-400" />}
                </div>
              </div>

              {/* Expanded stage details */}
              {expandedIdx === idx && (
                <div className="border-t border-slate-100 bg-slate-50/50">
                  <div className="px-5 py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Stage Details</p>
                    <div className="space-y-2">
                      {tracker.stages.map((stage, sIdx) => (
                        <div
                          key={sIdx}
                          className="flex items-center gap-4 p-3 bg-white rounded-lg border border-slate-100"
                          data-testid={`stage-${sIdx + 1}`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            stage.bonus_amount > 0 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                          }`}>
                            {stage.stage}
                          </div>
                          <div className="flex-1 grid grid-cols-5 gap-3 text-sm">
                            <div>
                              <p className="text-xs text-slate-400">Candidate</p>
                              <p className="font-medium text-slate-800 truncate">{stage.candidate}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400">{tab === 'vendor' ? 'Client' : 'Vendor'}</p>
                              <p className="font-medium text-slate-700 truncate">{tab === 'vendor' ? stage.client : stage.vendor}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400">Added By</p>
                              <p className="font-medium text-slate-700 truncate">{stage.created_by || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400">Date</p>
                              <p className="font-medium text-slate-700">{stage.start_date || stage.created_at?.split('T')[0] || '—'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-400">Bonus</p>
                              <p className="font-bold text-emerald-700">{formatCurrency(stage.bonus_amount)}</p>
                            </div>
                          </div>
                          <Badge className={
                            stage.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                            stage.status === 'draft' ? 'bg-slate-100 text-slate-600' :
                            'bg-blue-100 text-blue-700'
                          }>
                            {stage.status?.replace(/_/g, ' ').toUpperCase()}
                          </Badge>
                        </div>
                      ))}

                      {/* Remaining stages */}
                      {tracker.stages_remaining > 0 && (
                        <div className="flex items-center gap-3 p-3 bg-amber-50/50 rounded-lg border border-dashed border-amber-200">
                          <Clock size={18} className="text-amber-500" />
                          <p className="text-sm text-amber-700">
                            <span className="font-semibold">{tracker.stages_remaining}</span> more placement{tracker.stages_remaining > 1 ? 's' : ''} needed
                            <ArrowRight size={14} className="inline mx-1" />
                            {formatCurrency(tracker.remaining)} remaining bonus
                          </p>
                        </div>
                      )}

                      {tracker.stages_remaining === 0 && (
                        <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <CheckCircle size={18} className="text-emerald-600" weight="bold" />
                          <p className="text-sm text-emerald-700 font-semibold">All 6 stages complete — Full bonus of {formatCurrency(tracker.max_total)} earned!</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BonusTrackerPage;
